import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";
import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

/* ========= helpers ========= */
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const toLatexNumber = (raw) => (formatNumLatex(String(raw), "") || String(raw));

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

/** Ułamek (z redukcją do liczby całkowitej, gdy możliwe) */
function improperLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return String(N / D);       // 9/1 -> "9"
  const sign = N < 0 ? "-" : "";
  return `${sign}\\dfrac{${Math.abs(N)}}{${D}}`;
}

/** Liczba mieszana, jeśli ma sens; w p.p. null */
function mixedLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return null;                // całkowita
  const A = Math.floor(Math.abs(N) / D);
  const R = Math.abs(N) % D;
  if (A === 0) return null;                    // ułamek właściwy
  const sign = N < 0 ? "-" : "";
  return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
}

/** Preferuj mieszany; inaczej całkowita/„niewłaściwy” */
function finalPointLatex(num, den) {
  return mixedLatexFromFrac(num, den) || improperLatexFromFrac(num, den);
}

/* ========= generate ========= */
export function generateTask() {
  const pick = (min, max) =>
    (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * (max - min + 1)) + min);

  let c = 0; while (c === 0) c = pick(1, 6); // c ≠ 0
  const a = pick(1, 6);
  const b = pick(1, 10);
  const d = pick(1, 10);

  const leftNum = `${coeffX(a)} ${withSign(b)}`;
  const leftDen = `${coeffX(c)} ${withSign(d)}`;
  const latex = `\\dfrac{${leftNum}}{${leftDen}} = 0`;

  const x0 = simplifyFrac(-d, c); // wykluczony punkt
  const xr = simplifyFrac(-b, a); // pierwiastek licznika

  return {
    type: "RationalSolveAndDomainLinearTask",
    data: {
      a, b, c, d, latex,
      x0_num: x0.num, x0_den: x0.den, x0_val: x0.num / x0.den,
      xr_num: xr.num, xr_den: xr.den, xr_val: xr.num / xr.den,
      hasSolution: !(x0.num * xr.den === xr.num * x0.den), // xr ≠ x0
    },
  };
}

/* ========= task ========= */
export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Podaj dziedzinę i rozwiąż równanie: <InlineMath math={data.latex} /></p>
    </div>
  );
}

/* ========= input ========= */
export function renderInput(_, userInput = {}, onChange, disabled) {
  const frac = userInput?.x; // może być string (legacy) albo obiekt { raw, parsed }
  const hasRaw = typeof frac === "object" && !!frac?.raw;
  const preview = hasRaw ? formatFracPreview(frac.raw, "x") : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Dziedzina */}
      <div className="flex flex-col gap-2">
        <IntervalInput
          value={userInput?.D || ""}
          onChange={(val) => onChange({ ...userInput, D: val })}
          disabled={disabled}
          label="D"
          placeholder="np. ℝ\{3}  lub  ℝ\{3, 5}  lub  ℝ - {3}"
          asString
          previewWithX
          prefixAbove
        />
      </div>

      {/* Rozwiązanie równania */}
      <div className="flex flex-col gap-1">
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;="} />
        </div>

        <FractionInput
          value={hasRaw ? frac : (typeof frac === "string" ? frac : "")}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          placeholder='np. 2, 3/5, 1 2/3 lub brak'
          label="x"
        />

        {preview ? (
          <div style={{ pointerEvents: "none" }}>
            <strong>Podgląd:</strong> <InlineMath math={preview} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ========= helpers: "brak rozwiązań" (ujednolicone) =========
function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-") // różne myślniki → zwykły minus
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ") // wąskie spacje itp.
    .replace(/\s+/g, " ")
    .trim();
}

function matchesNoneKeyword(s) {
  if (!s) return false;
  const t = normalizeUserNumberText(s).toLowerCase();

  // zdejmujemy polskie znaki na potrzeby dopasowania słów-kluczy
  const noDia = t
    .replace(/ą/g, "a").replace(/ć/g, "c").replace(/ę/g, "e")
    .replace(/ł/g, "l").replace(/ń/g, "n").replace(/ó/g, "o")
    .replace(/ś/g, "s").replace(/ż/g, "z").replace(/ź/g, "z");

  return (
    t === "∅" ||
    t === "ø" || t === "Ø" ||
    noDia === "brak" ||
    noDia === "brak odpowiedzi" ||
    noDia === "brak rozwiazan" ||
    noDia === "pusty" ||
    noDia === "zbior pusty" ||
    t === "empty" ||
    t === "none" ||
    /^\{\s*\}$/.test(t)
  );
}


/* ========= check ========= */
export function checkAnswer({ data }, userInput = {}) {
  // dziedzina
  const rawD = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  const parsedD = parseIntervalInput(rawD);
  const x0 = data.x0_val;
  const eqTol = (x, y) => Math.abs(x - y) <= 0.01 + 1e-9;

  let domainOK = false;
  if (parsedD) {
    if (parsedD.kind === "complement") {
      domainOK = eqTol(parsedD.point, x0);
    } else if (parsedD.kind === "intervals") {
      const ints = parsedD.intervals;
      if (ints.length === 2) {
        const [I, J] = ints;
        const ok1 =
          I.left === -Infinity && !I.rightClosed && eqTol(I.right, x0) &&
          eqTol(J.left, x0) && !J.leftClosed && J.right === Infinity;
        const ok2 =
          J.left === -Infinity && !J.rightClosed && eqTol(J.right, x0) &&
          eqTol(I.left, x0) && !I.leftClosed && I.right === Infinity;
        domainOK = ok1 || ok2;
      }
    }
  }

  // rozwiązanie
  const hasSolution = data.hasSolution;
  const frac = userInput?.x;

  // --- "brak rozwiązań" ---
  const rawString =
    typeof frac === "string"
      ? frac
      : (typeof frac === "object" ? frac.raw : "");

  const wantNoneKeyword = matchesNoneKeyword(rawString);

  const isEmptyObj = typeof frac === "object" && !frac?.raw;
  const isEmptyStr =
    typeof frac === "string" &&
    normalizeUserNumberText(rawString) === "";

  const wantNone =
    wantNoneKeyword || (!hasSolution && (isEmptyObj || isEmptyStr));

  // --- liczba ---
  let parsedX = NaN;
  if (typeof frac === "object" && Number.isFinite(frac?.parsed)) {
    parsedX = frac.parsed;
  } else if (typeof frac === "string") {
    parsedX = parseRootFractionInput(
      normalizeUserNumberText(rawString).replace(",", ".")
    );
  }

  const xr = data.xr_val;

  let solutionOK = false;
  if (!hasSolution) {
    solutionOK = wantNone;
  } else {
    if (Number.isFinite(parsedX)) solutionOK = eqTol(parsedX, xr);
  }

  return {
    correct: domainOK && solutionOK,
    expected: { D: x0, x: hasSolution ? xr : null, hasSolution },
  };
}


/* ========= short answer ========= */
export function renderAnswer({ data }) {
  const x0L = finalPointLatex(data.x0_num, data.x0_den);
  const sol = data.hasSolution ? finalPointLatex(data.xr_num, data.xr_den) : null;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div><InlineMath math={`x\\in\\;\\mathbb{R}\\setminus\\{${x0L}\\}`} /></div>
      <div style={{ marginTop: "0.4rem" }}>
        {sol ? <InlineMath math={`x\\;=\\;${sol}`} /> : <i>Brak rozwiązań (pierwiastek wypada z dziedziny).</i>}
      </div>
    </div>
  );
}

/* ========= user answer preview ========= */
export function renderUserAnswer(userInput = {}) {
  const rawD = typeof userInput === "string"
    ? userInput
    : (userInput?.D?.raw || userInput?.D || "");

  const dLatex = intervalToLatex(rawD, { withX: true });

  // FractionInput: jeśli mamy obiekt z raw → ładny preview; w legacy-string użyj formatNumLatex
  const xObj = userInput?.x;
  const xLatex =
    (typeof xObj === "object" && xObj?.raw)
      ? formatFracPreview(xObj.raw, "x")
      : (typeof xObj === "string"
          ? (formatNumLatex(String(xObj), "") || String(xObj)).trim()
          : "");

  return (
    <div className="space-y-1">
      {rawD ? <div><InlineMath math={`\\displaystyle ${dLatex}`} /></div> : null}
      {xLatex ? <div><InlineMath math={xLatex} /></div> : null}
      {!rawD && !xLatex ? <i>(brak odpowiedzi)</i> : null}
    </div>
  );
}

/* ========= explanation ========= */
export function renderExplanation({ data }) {
  const { a, b, c, d, x0_num, x0_den, xr_num, xr_den, hasSolution } = data;

  const dL = toLatexNumber(d), cL = toLatexNumber(c),
        bL = toLatexNumber(b), aL = toLatexNumber(a);

  // operacje: warunek z mianownika
  const opSubD = d < 0 ? `+\\,${toLatexNumber(-d)}` : `-\\,${dL}`;
  const opDivC = c < 0 ? `:\\,(${cL})` : `:\\,${cL}`;

  // operacje: licznik
  const opSubB = b < 0 ? `+\\,${toLatexNumber(-b)}` : `-\\,${bL}`;
  const opDivA = a < 0 ? `:\\,(${aL})` : `:\\,${aL}`;

  const x0Final = finalPointLatex(x0_num, x0_den);
  const xrImp = improperLatexFromFrac(xr_num, xr_den);
  const xrMix = mixedLatexFromFrac(xr_num, xr_den);

  return (
    <div className="space-y-4">
      {/* Krok 1: warunek z mianownika + wykluczenie punktu */}
      <p><strong>Krok 1:</strong> Warunek z mianownika i wykluczenie punktu.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={String.raw`\begin{aligned}
          ${coeffX(c)} ${withSign(d)} &\neq 0   &\big|&\; ${opSubD} \\
          ${coeffX(c)} &\neq ${toLatexNumber(-d)} &\big|&\; ${opDivC}
        \end{aligned}`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x \\neq ${x0Final}`} />
      </div>

      {/* Krok 2: rozwiązanie – licznik=0 daje rozwiązanie */}
      <p><strong>Krok 2:</strong> Rozwiązujemy równanie z licznika —
        <span style={{ whiteSpace: "nowrap" }}> licznik równy zero jest rozwiązaniem równania.</span>
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={String.raw`\begin{aligned}
          ${coeffX(a)} ${withSign(b)} &= 0   &\big|&\; ${opSubB} \\
          ${coeffX(a)} &= ${toLatexNumber(-b)} &\big|&\; ${opDivA}
        \end{aligned}`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          xrMix
            ? String.raw`\begin{aligned} x &= ${xrImp} \\ x &= ${xrMix} \end{aligned}`
            : String.raw`x = ${xrImp}`
        } />
      </div>

      {/* Krok 3: odpowiedź + sprawdzenie przynależności do dziedziny */}
      <p>
        <strong>Krok 3:</strong> Odpowiedź — sprawdzamy, czy otrzymany wynik
        należy do dziedziny (musi być{" "}
        <InlineMath math={`x\\neq ${x0Final}`} />).
      </p>

      <div style={{ textAlign: "center", marginBottom: "0.25rem" }}>
        <BlockMath math={`\\mathbb{R}\\setminus\\{${x0Final}\\}`} />
      </div>

      <div style={{ textAlign: "center", marginTop: "-2px" }}>
        {hasSolution ? (
          <em>Należy do dziedziny, więc jest rozwiązaniem.</em>
        ) : (
          <em>Nie należy do dziedziny, więc rozwiązań brak.</em>
        )}
      </div>

      {hasSolution ? (
        <div style={{ textAlign: "center" }}>
          <BlockMath math={`x = ${finalPointLatex(xr_num, xr_den)}`} />
        </div>
      ) : (
        <div style={{ textAlign: "center" }}>
          <em>Brak rozwiązań, bo pierwiastek wypada z dziedziny.</em>
        </div>
      )}
    </div>
  );
}
